import { type LucideIcon, Inbox } from 'lucide-react';
import { isValidElement } from 'react';

interface EmptyStateProps {
  icon?: LucideIcon | React.ReactNode;
  title: string;
  description?: string;
  action?: React.ReactNode | { label: string; onClick: () => void };
}

export function EmptyState({
  icon: IconProp = Inbox,
  title,
  description,
  action,
}: EmptyStateProps) {
  // Handle both LucideIcon and ReactNode for icon
  const renderIcon = () => {
    if (isValidElement(IconProp)) {
      // It's already a ReactNode (e.g., <Users className="..." />)
      return (
        <div className="p-4 bg-slate-800/50 rounded-full mb-4 text-slate-500">
          {IconProp}
        </div>
      );
    }
    // It's a LucideIcon component
    const Icon = IconProp as LucideIcon;
    return (
      <div className="p-4 bg-slate-800/50 rounded-full mb-4">
        <Icon className="h-8 w-8 text-slate-500" />
      </div>
    );
  };

  // Handle both ReactNode and action object
  const renderAction = () => {
    if (!action) return null;
    if (isValidElement(action)) {
      return <div className="mt-2">{action}</div>;
    }
    // It's an action object with label and onClick
    const actionObj = action as { label: string; onClick: () => void };
    return (
      <div className="mt-2">
        <button
          onClick={actionObj.onClick}
          className="px-4 py-2 bg-cyan-600 hover:bg-cyan-500 text-white font-medium rounded-lg transition-colors"
        >
          {actionObj.label}
        </button>
      </div>
    );
  };

  return (
    <div className="flex flex-col items-center justify-center py-12 px-4">
      {renderIcon()}
      <h3 className="text-lg font-medium text-white mb-1">{title}</h3>
      {description && (
        <p className="text-sm text-slate-400 text-center max-w-sm mb-4">
          {description}
        </p>
      )}
      {renderAction()}
    </div>
  );
}
